import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import _ from 'lodash'
import { TagPicker, ValidationState } from '@fluentui/react/lib/Pickers'
import { tagHelper } from '../../common/src/helpers'
import { validateTag } from '../../common/src/helpers/stringHelper'

export const getTagNameFromItem = item => item.name

export const createItemFromTag = tag => {
  const name = tagHelper.getName(tag)
  return { key: name, name: name }
}

export const createItemFromName = name => {
  const trimmedText = _.trim(name, ' \n')
  return { key: trimmedText, name: trimmedText }
}

const pickerSuggestionsProps = {
  suggestionsHeaderText: 'Suggested Tags',
  noResultsFoundText: 'No Tags Found',
}

export const SuggestionsTagPicker = memo(
  ({ items, suggestedItems, autoFocus = false, onInsert, onRemove, onError, onFinish, ...rest }) => {
    const tagPickerRef = useRef(null)
    const sortedItems = useMemo(() => _.sortBy(items, 'name'), [items])
    const sortedSuggestedItems = useMemo(() => _.sortBy(suggestedItems, 'name'), [suggestedItems])

    useEffect(() => {
      if (autoFocus) {
        tagPickerRef.current?.input?.current?.focus()
      }
    }, [autoFocus, tagPickerRef])

    const onChange = useCallback(
      async selection => {
        const actionIsInsert = selection.length > sortedItems.length
        const diff = actionIsInsert
          ? _.differenceBy(selection, sortedItems, getTagNameFromItem)
          : _.differenceBy(sortedItems, selection, getTagNameFromItem)
        const i = _.first(diff)
        const tagName = i && getTagNameFromItem(i)
        if (tagName) {
          const action = actionIsInsert ? onInsert : onRemove
          await action(tagName)
        }
        tagPickerRef.current?.input?.current?.focus()
      },
      [sortedItems, onInsert, onRemove]
    )

    const onResolveSuggestions = useCallback(
      text => {
        text = _.toLower(text)

        // Filter by not added
        const notAddedSuggestions = _.filter(sortedSuggestedItems, item => {
          return !_.some(sortedItems, sItem => sItem.name === item.name)
        })

        if (text) {
          // Filter by name
          return _.filter(notAddedSuggestions, item => {
            return _.includes(_.toLower(item.name), text)
          })
        } else {
          return notAddedSuggestions
        }
      },
      [sortedItems, sortedSuggestedItems]
    )

    const onValidateInput = useCallback(
      text => {
        const trimmedText = _.trim(text, ' \n')
        if (validateTag(trimmedText)) {
          if (_.some(sortedItems, item => getTagNameFromItem(item) === trimmedText)) {
            onError('This tag already is added.')
            return ValidationState.invalid
          }
          onError(null)
          return ValidationState.valid
        } else if (_.isEmpty(trimmedText)) {
          onFinish && onFinish()
          return ValidationState.invalid
        } else {
          onError('Please type a valid tag.')
          return ValidationState.invalid
        }
      },
      [sortedItems, onError, onFinish]
    )

    const onDismiss = useCallback(evt => {
      // prevent weird auto-selection
      // Reported bug https://sync.appfluence.com/native/api/v1/item/13667427/
      // Github issue https://sync.appfluence.com/native/api/v1/item/11547250/
      evt.preventDefault()
    }, [])

    return (
      <TagPicker
        selectedItems={sortedItems}
        pickerSuggestionsProps={pickerSuggestionsProps}
        createGenericItem={createItemFromName}
        onValidateInput={onValidateInput}
        onChange={onChange}
        getTextFromItem={getTagNameFromItem}
        onResolveSuggestions={onResolveSuggestions}
        onDismiss={onDismiss}
        componentRef={tagPickerRef}
        {...rest}
      />
    )
  }
)
